import { Component, OnInit, ViewRef, ChangeDetectorRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddEditUsersComponent } from '../users/add-edit-users/add-edit-users.component';
import { UsersGridService } from './users-grid.service';
import { Subscription } from 'rxjs/Subscription';
import { GridRequestParams } from '../shared/_models/GridRequestParams';
import { Angular2Csv } from 'angular2-csv';
import { GridDataResult, PageChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { Role } from './_models/Role';
import { UsersService } from './_services/users.service';
import { DeleteConfirmationComponent } from '../shared/components/delete-confirmation/delete-confirmation.component';
import { LoggerService } from '../core/logger/global-logger.service';
import { SortDescriptor } from '@progress/kendo-data-query/dist/es/sort-descriptor';
import { DatePipe } from '@angular/common';
import { debounceTime } from 'rxjs/operators';
import { ApplicationInsightsService } from '../_services/applicationInsights.service';
import { LocalCacheService } from '../core/local-cache/local-cache.service';
import { TranslateService } from '@ngx-translate/core';
import { SubheaderHandlerService } from '../shared/services/subheader-handler.service';

@Component({
   selector: 'app-users-list',
   templateUrl: './users-list.component.html'
})
export class UsersListComponent implements OnInit, AfterViewInit, OnDestroy {
   @ViewChild('usersGrid') grid: GridComponent;
   gridViewData: GridDataResult;
   modalRef: any;
   gridPageLoad = false;
   apiSubscription: Subscription;
   isLoading = true;
   isScroll: boolean;
   translatedText = [];
   searchOptions: GridRequestParams = {
      pageSize: 35,
      paginationStartIndex: 0,
      searchBy: '',
      sortBy: { sortOrder: 1, sortColumn: 'displayName' }
   };
   exportCsvData: any[];
   usersGridCSV: {};
   usersCSVData: any[] = [];
   pageSize = 35;
   skip = 0;
   data: any;
   roles: Role[];
   sort: SortDescriptor[] = [{
      field: 'displayName',
      dir: 'asc'
   }];
   totalCount: number;
   pageIndex = 1;
   lastSkipedNumber = 0;
   pipe = new DatePipe('en-US');
   public subheaderData = {
      leftSection: {
         type: 'breadcrumb',
         data: [
            {
               text: '',
               link: '/home'
            },
            {
               text: 'users',
               link: ''
            }
         ]
      }
   };
   constructor(
      private cdr: ChangeDetectorRef,
      private modalService: NgbModal,
      private usersGridService: UsersGridService,
      private usersService: UsersService,
      private appInsights: ApplicationInsightsService,
      private storage: LocalCacheService,
      private logger: LoggerService,
      private translate: TranslateService,
      private subHeaderService: SubheaderHandlerService) {
      translate.get([
         'COMMON.HOME',
         'SIDE_BAR.USERS',
         'COMMON.WARNING'
      ]).subscribe((translations: string) => {
         this.subheaderData.leftSection.data[0].text = translations['COMMON.HOME'];
         this.subheaderData.leftSection.data[1].text = translations['SIDE_BAR.USERS'];
         this.translatedText[0] = translations['COMMON.WARNING'];
         this.subHeaderService.updateLeftSection(this.subheaderData.leftSection);
      });

   }

   ngOnInit() {
      this.appInsights.logPageView('Users', window.location.href, { accountId: this.storage.fetchData('loggedEmailId') });
      this.getUsersGridData();
      this.data = this.usersGridService;
      this.getRoles();
   }

   ngAfterViewInit() {
      if (this.grid !== undefined) {
         this.grid.pageChange.pipe(debounceTime(500)).subscribe(event => {
            this.pageChange(event);
         });
      }
   }

   public pageChange(event: PageChangeEvent): void {
      this.searchOptions.paginationStartIndex = event.skip;
      this.getUsersGridData(true);
   }

   private loadUsers(): void {
      this.gridViewData = {
         data: this.data !== undefined ? this.data.gridDataList : [],
         total: this.data !== undefined ? this.data.totalRecords : 0
      };
      this.isLoading = false;
      this.isScroll = false;
   }

   public getUsersGridData(pageChange = false, useForCsv = false) {
      this.isScroll = pageChange;
      this.usersGridService.errorCode = 0;
      this.gridPageLoad = pageChange;
      if (this.apiSubscription) {
         this.apiSubscription.unsubscribe();
      }

      this.apiSubscription = this.usersGridService.query(this.searchOptions, useForCsv).subscribe(e => {
         this.detectChangeReady();
         if (this.usersGridService.errorCode === 200) {
            this.data = this.usersGridService;
            this.totalCount = this.usersGridService.totalRecords;
         } else {
            this.data = undefined;
         }
         this.gridPageLoad = false;
         this.loadUsers();
      }, error => {
      });
   }

   detectChangeReady() {
      if (!(this.cdr as ViewRef).destroyed) {
         this.cdr.detectChanges();
      }
   }

   filterData($event) {
      this.searchOptions.searchBy = $event;
      this.searchOptions.paginationStartIndex = 0;
      this.searchOptions.pageSize = 35;
      if (this.searchOptions.searchBy.length === 0 || this.searchOptions.searchBy.length > 2) {
         this.getUsersGridData();
      }
   }

   sortChange(sort: SortDescriptor[]): void {
      this.sort = sort;
      if (this.sort[0].dir === 'asc') {
         this.searchOptions.sortBy.sortOrder = 1;
         this.searchOptions.sortBy.sortColumn = this.sort[0].field;
      } else {
         this.searchOptions.sortBy.sortOrder = 2;
         this.searchOptions.sortBy.sortColumn = this.sort[0].field;
      }
      this.searchOptions.paginationStartIndex = 0;
      this.searchOptions.pageSize = 35;
      this.getUsersGridData();
   }

   addUsers() {
      this.modalRef = this.modalService.open(AddEditUsersComponent, {
         backdrop: 'static',
         keyboard: false,
         windowClass: 'add-user-modal',
      }).componentInstance;
      this.modalRef.roles = this.roles;
      this.modalRef.saveModalEmitter.subscribe(($event) => {
         this.getUsersGridData();
      });
   }

   editUser(id: number) {
      this.usersService.getUserById(id).subscribe(response => {
         this.modalRef = this.modalService.open(AddEditUsersComponent, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'add-user-modal',
         }).componentInstance;
         this.modalRef.userDetail = response;
         this.modalRef.roles = this.roles;
         this.modalRef.saveModalEmitter.subscribe(($event) => {
            this.getUsersGridData();
         });
      });
   }

   deleteUser(userDetail) {
      this.modalRef = this.modalService.open(DeleteConfirmationComponent, {
         backdrop: 'static',
         keyboard: false,
         windowClass: 'delete-modal'
      }).componentInstance;
      this.modalRef.modalText = this.translatedText[0];
      this.modalRef.modContent = 'Are you sure you want to delete the User?';
      this.modalRef.btnPrimary = { text: 'Delete', isPrimary: true };
      this.modalRef.btnSecondary = { text: 'Cancel', isPrimary: false };
      this.modalRef.isWarning = true;
      this.modalRef.showClose = false;
      this.modalRef.btnPrimaryClickEmitter.subscribe(($event) => {
         this.isLoading = true;
         this.usersService.deleteUser(userDetail.id).subscribe(response => {
            if (response) {
               this.showToaster('Success', 'User Deleted');
               this.getUsersGridData();
            } else {
               this.showToaster('Error', 'User not Deleted');
            }
         });
      });
   }

   private generateUsersCSV() {
      this.exportCsvData = new Array<any>();
      this.usersGridService.getAllUsersData(this.searchOptions, true).subscribe(e => {
         if (this.usersGridService.exportCsvData !== undefined) {
            if (this.usersGridService.usersDetails !== undefined) {
               for (const user of this.usersGridService.usersDetails) {
                  const CSVRow = {};
                  CSVRow['User Name'] = (user['displayName'] !== null ? user['displayName'] : '');
                  CSVRow['Email'] = user['emailId'];
                  CSVRow['Roles'] = user['roleName'];
                  CSVRow['Created By'] = (user['createdBy'] !== null ? user['createdBy'] : '');
                  CSVRow['Created Date'] = this.pipe.transform(user['createdAt'], 'dd/MM/yyyy');
                  this.exportCsvData.push(CSVRow);
               }
               if (this.exportCsvData.length > 0) {
                  // tslint:disable-next-line:no-unused-expression
                  new Angular2Csv(this.exportCsvData, 'Users', { headers: Object.keys(this.exportCsvData[0]) });
               }
            }
         }
      });
      this.usersGridCSV = this.usersCSVData;
   }

   kabobSelection(eventType: any) {
      switch (eventType.value) {
         case 'csv':
            this.generateUsersCSV();
            break;
      }
   }

   getRoles() {
      this.usersService.getRoles().subscribe(response => {
         this.roles = response.result;
      });
   }

   private showToaster(type: string, msg: string) {
      if (type === 'Success') {
         this.logger.success({ message: msg }, true, false);
      } else {
         this.logger.warning({ message: msg }, true, false);
      }
   }
   ngOnDestroy() {
      this.apiSubscription.unsubscribe();
      if (this.modalRef !== undefined) {
         this.modalRef.activeModal.close();
      }
   }
}



// WEBPACK FOOTER //
// D:/a/1/s/Fed/src/app/users/users-list.component.ts